import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CATEGORY_STYLES } from "../data/events";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function EventsCalendar({ events }) {
  // default to the month of the earliest event
  const firstDate = events.length ? new Date(events[0].date + "T00:00:00") : new Date(2026, 8, 1);
  const [cursor, setCursor] = useState({ y: firstDate.getFullYear(), m: firstDate.getMonth() });

  const byDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      (map[e.date] = map[e.date] || []).push(e);
    });
    return map;
  }, [events]);

  const { y, m } = cursor;
  const first = new Date(y, m, 1);
  let startDow = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(y, m + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const iso = (d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div className="rounded-lg border border-border/60 bg-secondary/10 p-3">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-[15px] font-semibold text-foreground">
          {MONTHS[m]} {y}
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCursor(m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 })}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-secondary/40 text-foreground hover:bg-secondary"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => setCursor(m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 })}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-secondary/40 text-foreground hover:bg-secondary"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {DOW.map((d) => (
          <div key={d} className="pb-1 text-center text-[11px] font-medium text-muted-foreground">
            {d}
          </div>
        ))}
        {cells.map((d, i) => {
          const dayEvents = d ? byDay[iso(d)] || [] : [];
          return (
            <div
              key={i}
              className={`min-h-[92px] rounded-md border p-1.5 ${
                d ? "border-border/60 bg-background/40" : "border-transparent"
              }`}
            >
              {d && (
                <>
                  <div className="mb-1 text-[11px] font-medium text-muted-foreground">{d}</div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((e) => (
                      <a
                        key={e.id}
                        href={e.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={e.title}
                        className={`block truncate rounded px-1 py-0.5 text-[10px] leading-tight ${
                          CATEGORY_STYLES[e.category] || CATEGORY_STYLES.Meetup
                        }`}
                      >
                        {e.time} {e.title}
                      </a>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="px-1 text-[10px] text-muted-foreground">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
